import { useState, memo } from "react"
import { ComboboxOption, ComboboxOptions, Label, Radio, RadioGroup } from '@headlessui/react'
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { useNavigate } from "../navigation";
import Fuse from "fuse.js";
import _ from "lodash";
import { family_member, patent_family, trade_mark, trademark_family } from "../data";
import { useRoles } from "../user/Auth";
import { decorateMatches, extractLink } from "./utils";
import { useFilter } from "../filter/Filter";
import { free_text_filter, reference_filter } from "../filter/utils";
import { IconBox, IconCalendar, IconLightBulb, IconShieldCheck, IconTradeMark } from "../components/icons";
import { FunnelIcon } from "@heroicons/react/20/solid";
import { ListBulletIcon } from "@heroicons/react/24/solid";
import { useIpSearch } from "./search";


const SearchResults = memo(function SearchResults({ query, focus, search }: { query: string, focus: string, search: (searchTerm: string) => any }) {
    const {t} = useTranslation()

    const lc_query = query?.toLowerCase()
    const results = query.length > 0 ? search(lc_query) : { families: [], members: [], commodities: [], inventions: [], trademarks: [], trademarkFamilies: [], tasks: [] }

    const searchResults = (query === '' || results === undefined)
        ? []
        : _([
            ...results.families,
            ...results.members,
            ...results.commodities,
            ...results.inventions,
            ...results.trademarks,
            ...results.trademarkFamilies,
            ...results.tasks,
        ])
            .filter(r => focus === 'all' || r.type === focus)
            .map((result) => ({ ...result, ...(extractLink(result.type, result.item) ?? {}) }))
            .filter(r => r.to !== undefined && r.reference !== undefined)
            .sortBy('score')
            .value() as ResultProps[]

    //console.log({searchResults, results})

    return (
      <>
        <ComboboxOptions static className="flex flex-col-reverse">
          {searchResults.length > 0 && (
            <div className="max-h-[60vh] scroll-py-2 overflow-y-auto p-2 text-sm">
              {searchResults.map((result, i) => (
                <SearchResult key={i} first={i === 0} {...{ result: { ...result } }} />
              ))}
            </div>
          )}
          {query !== '' && (
            <div className="p-2 text-sm">
              <QueryActions {...{ query }} />
            </div>
          )}
        </ComboboxOptions>
        {query !== '' && searchResults?.length === 0 && (
          <div className="px-6 py-14 text-center sm:px-14">
            {/* <FolderIcon className="mx-auto h-6 w-6 text-gray-900 text-opacity-40" aria-hidden="true" /> */}
            <p className="mt-4 text-sm text-gray-900">{t('no-results-try-again')}</p>
          </div>
        )}
      </>
    )
})

type ResultProps = Fuse.FuseResult<any> & {type: string, to: string, reference: string}

function comboboxOptionStyle(active: boolean) {
    return clsx(
        "text-sm text-gray-700 px-3 py-2 block rounded-md hover:bg-gray-900 hover:bg-opacity-5 hover:text-gray-900",
        "flex flex-row gap-4 items-center",
        active && "bg-gray-900 bg-opacity-5 text-gray-900"
    )
}

function SearchResult({ result, first = false }: { result: ResultProps, first?: boolean }) {
    const {t} = useTranslation()

    return (
        <ComboboxOption className='cursor-pointer group'  value={result}>{({ focus }) =>
            <div className={comboboxOptionStyle(focus)} >
                <div className="w-6">
                    {typeIcons[result.type]}
                </div>
                <div>
                    <div className={clsx("font-medium", focus ? "text-pcx-900" : "text-pcx-700")}>
                        <span className="group-hover:underline">{result?.reference}</span> <span className={clsx(focus ? "text-gray-900" : "text-gray-700", "font-base")}>({t(result.type)})</span>
                    </div>
                    <RenderMatch match={result?.matches?.[0]} active={focus} />
                </div>
            </div>
        }</ComboboxOption>
    )
}

function RenderMatch({match, active}: {match?: Fuse.FuseResultMatch, active: boolean}) {
    if (!match) return null

    const rendered = decorateMatches(match)

    return <div className={clsx("pt-1", active ? "text-gray-900" : "text-gray-700")} dangerouslySetInnerHTML={{__html: rendered}} />
}

const typeIcons = {
    [patent_family]: <IconShieldCheck />,
    [family_member]: <IconShieldCheck />,
    [trade_mark]: <IconTradeMark />,
    [trademark_family]: <IconTradeMark />,
    'invention': <IconLightBulb />,
    'commodity': <IconBox />,
    'task': <IconCalendar />,
}

interface QueryAction {
    callback: () => void
}

function QueryActions({query}: {query: string}) {
    const {t} = useTranslation()
    const {addFilter} = useFilter()

    function addFreeTextFilter() {
        addFilter({name: free_text_filter, config: query, isActive: true})
    }
    function addReferenceFilter() {
        const config = query.split(',').map(r => r.trim()).join('\n')
        addFilter({name: reference_filter, config, isActive: true})
    }

    const actions = [
        {callback: addFreeTextFilter,  label: t('add-query-as-free-text-filter', {query}), icon: <FunnelIcon className="h-5 w-5 opacity-80" />},
        {callback: addReferenceFilter, label: t('add-query-as-reference-filter', {query}), icon: <ListBulletIcon className="h-5 w-5" />},
    ]

    return <>
        {actions.map((action, i) =>
            <ComboboxOption key={i} value={action}>{({ focus }) =>
                <div className={comboboxOptionStyle(focus)}>
                    <div className="w-6">
                        {action.icon}
                    </div>
                    <div className="font-medium text-pcx-700">
                        {action.label}
                    </div>
                </div>
            }</ComboboxOption>)}
    </>
}

export function useHandleIpResults() {
    const navigate = useNavigate()

    function handleIpResult(result: { to: string } | QueryAction) {
        if ('to' in result) {
            navigate(result.to);
        } else {
            result.callback()
        }
    }

    return {handleIpResult}
}


export function IpResults({query}: {query: string}) {
    const {t} = useTranslation()
    const {hasBrands, hasInnovation, hasTasks} = useRoles()
    const {search} = useIpSearch()

    const possibleFocus = [
        'all',
        patent_family,
        family_member,
        'commodity',
        hasInnovation && 'invention',
        hasBrands && trade_mark,
        hasBrands && trademark_family,
        hasTasks && 'task',
    ].filter(Boolean)

    const [focus, setFocus] = useState(possibleFocus[0])

    return <>
        {query !== '' &&
            <RadioGroup value={focus} onChange={setFocus} as='div' className='px-4 py-2'>
                <Label className='font-medium text-slate-600 text-xs px-px'>{t('focus-on')}</Label>
                <div className="flex flex-row flex-wrap gap-2 text-xs py-1.5">
                    {possibleFocus.map((f, i) =>
                        <Radio key={i} value={f}>{({ focus, checked }) =>
                            <div className={clsx(
                                "px-2 py-1 rounded-sm hover:bg-gray-900 hover:bg-opacity-5 cursor-pointer whitespace-nowrap",
                                focus && "bg-gray-900 bg-opacity-5",
                                checked && "bg-pcx-400 bg-opacity-20"
                            )}>
                                {t(f)}
                            </div>
                        }</Radio>)}
                </div>
            </RadioGroup>}
        <SearchResults {...{ query, focus, search }} />
    </>
}